import { ThemeProvider, loadStyleTheme } from "@tm/context-distribution"
import { OAuthTokenIds } from "@tm/models"
import { elementId, hasValidStoredLogin } from "@tm/utils"
import { createBrowserHistory } from "history"
import { Route, Router, Switch } from "react-router"
import { createRoot } from "react-dom/client"
import { getCatalogTexts } from "../data/translations"
import { initMock } from "../mocks"
import { loadBaseConfig } from "./data/loadBaseConfig"
import { loadConfiguration } from "./data/loadConfiguration"
import { loadSprites } from "./data/loadSprites"
import { loadStyles } from "./data/loadStyles"
import { resetLoadProgress } from "./data/loadingProgress"
import { getSavedCatalog } from "./document/currentCatalogStorage"
import { getLocalizationProvider } from "./localization/getLocalizationProvider"
import { initLogging } from "./logging/initLogging"
import { storeToken } from "./login/storeToken"
import { OAuthComponent } from "./oauth/OAuthComponent"
import { getStylesUrl } from "./theming/getStylesUrl"
import { getThemeUrl } from "./theming/getThemeUrl"
import { useHasConcept } from "./theming/useHasConcept"
import { initializeLocalization } from "./localization/initializeLocalization"

export const SimplePortalRoutes = ["/oauth"]

export async function initializeSimplePortal() {
    const appContainer = document.querySelector("#app")
    if (!appContainer) {
        return
    }

    const root = createRoot(appContainer, { identifierPrefix: elementId("root") })

    initLogging()
    initMock()

    // If NEXT was opened in a new tab, the session storage may not be shared from the parent tab.
    // So check if the token and language were passed as query parameters.
    if (!hasValidStoredLogin()) {
        const params = new URLSearchParams(location.search)
        const token = params.get("token")
        const languageId = params.get("languageId")

        if (token && languageId) {
            storeToken(token, languageId)
        }
    }

    const appConfig = await loadConfiguration(await loadBaseConfig(), "APP", getSavedCatalog(), location.hostname, undefined)

    if (!appConfig) {
        return
    }

    const { languagesConfig, localizationConfig } = await initializeLocalization(appConfig)

    const hasConcept = useHasConcept(appConfig)

    loadStyles(getStylesUrl(appConfig.styles, hasConcept))
    loadSprites(appConfig.icons)

    await loadStyleTheme(getThemeUrl(appConfig.styles, hasConcept))
    const LocalizationProvider = await getLocalizationProvider(
        appConfig,
        languagesConfig,
        localizationConfig,
        getCatalogTexts.bind(undefined, appConfig.params.translationsServiceUrl, appConfig.login.authId)
    )

    resetLoadProgress()

    root.render(
        <ThemeProvider>
            <LocalizationProvider>
                <Router history={createBrowserHistory()}>
                    <Switch>
                        <Route
                            path="/oauth/:tokenId"
                            render={(props) => (
                                <OAuthComponent
                                    tokenId={props.match.params.tokenId as OAuthTokenIds}
                                    config={appConfig.params.oAuthConfiguration}
                                    isProd={appConfig.params.env === "prod"}
                                />
                            )}
                        />
                    </Switch>
                </Router>
            </LocalizationProvider>
        </ThemeProvider>
    )
}
